import React, { useState, useEffect, useContext } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { AuthContext } from '../App';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import axios from 'axios';
import { toast } from 'sonner';
import { isPrivateFileReference, PrivateFileLink } from '../components/PrivateFileAccess';
import { ArrowLeft, User, Briefcase, MessageSquare, Star, Activity, Mail, Phone, Calendar, Shield, CheckCircle, Clock, AlertCircle, RefreshCw, CreditCard, DollarSign } from 'lucide-react';

const AdminUserDetails = () => {
  const navigate = useNavigate();
  const { userId } = useParams();
  const { API, language } = useContext(AuthContext);
  const [userDetails, setUserDetails] = useState(null);
  const [loading, setLoading] = useState(true);

  const content = {
    en: {
      title: 'User Details',
      back: 'Back to Users',
      overview: 'Overview',
      jobs: 'Jobs',
      bids: 'Bids',
      support: 'Support Tickets',
      reviews: 'Reviews',
      payments: 'Payments',
      totalPaid: 'Total Paid',
      transactionType: 'Type',
      amount: 'Amount',
      paymentStatus: 'Status',
      paymentDate: 'Date',
      savedCard: 'Saved Card',
      cardNumber: 'Card',
      autoRenewal: 'Auto-Renewal',
      enabled: 'Enabled',
      disabled: 'Disabled',
      registeredOn: 'Registered On',
      noSavedCard: 'No saved card on file',
      noPayments: 'No payments made yet',
      personalInfo: 'Personal Information',
      name: 'Name',
      email: 'Email',
      phone: 'Phone',
      role: 'Role',
      status: 'Status',
      subscription: 'Subscription',
      joinedDate: 'Joined Date',
      statistics: 'Statistics',
      totalJobs: 'Total Jobs',
      totalBids: 'Total Bids',
      totalTickets: 'Support Tickets',
      averageRating: 'Average Rating',
      client: 'Client',
      professional: 'Professional',
      admin: 'Admin',
      verified: 'Verified',
      pending: 'Pending',
      rejected: 'Rejected',
      trial: 'Trial',
      active: 'Active',
      inactive: 'Inactive',
      jobTitle: 'Job Title',
      jobStatus: 'Status',
      jobBudget: 'Budget',
      jobCategory: 'Category',
      createdAt: 'Created',
      bidAmount: 'Bid Amount',
      bidMessage: 'Message',
      bidStatus: 'Status',
      ticketTitle: 'Title',
      ticketSubject: 'Subject',
      ticketStatus: 'Status',
      reviewRating: 'Rating',
      reviewComment: 'Comment',
      reviewedBy: 'Reviewed By',
      noData: 'No data available',
      verification: 'Verification Status',
      categories: 'Categories',
      serviceAreas: 'Service Areas',
      open: 'Open',
      assigned: 'Assigned',
      completed: 'Completed',
      accepted: 'Accepted',
      new: 'New',
      inProgress: 'In Progress',
      resolved: 'Resolved'
    },
    de: {
      title: 'Benutzerdetails',
      back: 'Zurück zu Benutzern',
      overview: 'Übersicht',
      jobs: 'Aufträge',
      bids: 'Angebote',
      support: 'Support-Tickets',
      reviews: 'Bewertungen',
      payments: 'Zahlungen',
      totalPaid: 'Gesamt bezahlt',
      transactionType: 'Typ',
      amount: 'Betrag',
      paymentStatus: 'Status',
      paymentDate: 'Datum',
      savedCard: 'Gespeicherte Karte',
      cardNumber: 'Karte',
      autoRenewal: 'Automatische Verlängerung',
      enabled: 'Aktiviert',
      disabled: 'Deaktiviert',
      registeredOn: 'Registriert am',
      noSavedCard: 'Keine gespeicherte Karte',
      noPayments: 'Noch keine Zahlungen',
      personalInfo: 'Persönliche Informationen',
      name: 'Name',
      email: 'E-Mail',
      phone: 'Telefon',
      role: 'Rolle',
      status: 'Status',
      subscription: 'Abonnement',
      joinedDate: 'Beitrittsdatum',
      statistics: 'Statistiken',
      totalJobs: 'Gesamtaufträge',
      totalBids: 'Gesamtangebote',
      totalTickets: 'Support-Tickets',
      averageRating: 'Durchschnittsbewertung',
      client: 'Kunde',
      professional: 'Fachkraft',
      admin: 'Admin',
      verified: 'Verifiziert',
      pending: 'Ausstehend',
      rejected: 'Abgelehnt',
      trial: 'Testversion',
      active: 'Aktiv',
      inactive: 'Inaktiv',
      jobTitle: 'Auftragstitel',
      jobStatus: 'Status',
      jobBudget: 'Budget',
      jobCategory: 'Kategorie',
      createdAt: 'Erstellt',
      bidAmount: 'Angebotsbetrag',
      bidMessage: 'Nachricht',
      bidStatus: 'Status',
      ticketTitle: 'Titel',
      ticketSubject: 'Betreff',
      ticketStatus: 'Status',
      reviewRating: 'Bewertung',
      reviewComment: 'Kommentar',
      reviewedBy: 'Bewertet von',
      noData: 'Keine Daten verfügbar',
      verification: 'Verifizierungsstatus',
      categories: 'Kategorien',
      serviceAreas: 'Servicegebiete',
      open: 'Offen',
      assigned: 'Zugewiesen',
      completed: 'Abgeschlossen',
      accepted: 'Akzeptiert',
      new: 'Neu',
      inProgress: 'In Bearbeitung',
      resolved: 'Gelöst'
    }
  };

  const t = content[language] || content.en;

  useEffect(() => {
    fetchUserDetails();
  }, [userId]);

  const fetchUserDetails = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/admin/users/${userId}/details`);
      setUserDetails(response.data);
    } catch (error) {
      toast.error('Failed to load user details');
      console.error(error);
      navigate('/admin/users');
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const colors = {
      verified: 'bg-green-600',
      pending: 'bg-orange-600',
      rejected: 'bg-red-600',
      active: 'bg-green-600',
      trial: 'bg-blue-600',
      inactive: 'bg-gray-600'
    };
    return <Badge className={colors[status] || 'bg-gray-600'}>{t[status] || status}</Badge>;
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString(language === 'de' ? 'de-DE' : 'en-US');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!userDetails) return null;

  const { user, stats, jobs, bids, support_tickets, reviews_received, reviews_given, professional_profile, payments, saved_card, documents } = userDetails;
  const isProfessionalCandidate = user?.role === 'professional' || user?.roles?.includes('professional') || user?.pending_role_setup === 'professional';

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex flex-col gap-3 sm:flex-row sm:justify-between sm:items-center">
          <div className="flex min-w-0 items-center space-x-3">
            <User className="w-8 h-8 text-blue-600" />
            <div>
              <h1 className="text-2xl font-bold font-heading">{t.title}</h1>
              <p className="truncate text-sm text-gray-600">{user.name}</p>
            </div>
          </div>
          <Button variant="outline" onClick={() => navigate('/admin/users')}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            {t.back}
          </Button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* User Header Card */}
        <Card className="mb-6">
          <CardContent className="p-6">
            <div className="flex flex-col gap-5 sm:flex-row sm:items-start sm:justify-between">
              <div className="flex min-w-0 items-center space-x-4">
                <div className="w-16 h-16 sm:w-20 sm:h-20 shrink-0 rounded-full bg-blue-100 flex items-center justify-center">
                  <User className="w-10 h-10 text-blue-600" />
                </div>
                <div className="min-w-0">
                  <h2 className="break-words text-xl font-bold sm:text-2xl">{user.name}</h2>
                  <div className="flex flex-wrap items-center gap-2 mt-2">
                    {user.is_admin && <Badge className="bg-purple-600"><Shield className="w-3 h-3 mr-1" />{t.admin}</Badge>}
                    <Badge variant="outline">{t[user.role]}</Badge>
                    {isProfessionalCandidate && documents && getStatusBadge(documents.verification_status || 'incomplete')}
                  </div>
                </div>
              </div>
              <div className="min-w-0 text-left sm:text-right">
                <div className="flex items-center space-x-2 text-sm text-gray-600 mb-2">
                  <Mail className="w-4 h-4" />
                  <span className="break-all">{user.email}</span>
                </div>
                {user.phone && (
                  <div className="flex items-center space-x-2 text-sm text-gray-600 mb-2">
                    <Phone className="w-4 h-4" />
                    <span>{user.phone}</span>
                  </div>
                )}
                <div className="flex items-center space-x-2 text-sm text-gray-600">
                  <Calendar className="w-4 h-4" />
                  <span>{formatDate(user.created_at)}</span>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Statistics Cards */}
        <div className="grid md:grid-cols-4 gap-4 mb-6">
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">{t.totalPaid}</p>
                  <p className="text-2xl font-bold text-green-700" data-testid="stat-total-paid">CHF {(stats.total_paid_chf || 0).toFixed(2)}</p>
                </div>
                <DollarSign className="w-8 h-8 text-green-600" />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">{t.totalJobs}</p>
                  <p className="text-2xl font-bold">{stats.total_jobs}</p>
                </div>
                <Briefcase className="w-8 h-8 text-blue-600" />
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">{t.totalBids}</p>
                  <p className="text-2xl font-bold">{stats.total_bids}</p>
                </div>
                <Activity className="w-8 h-8 text-purple-600" />
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">{t.totalTickets}</p>
                  <p className="text-2xl font-bold">{stats.total_support_tickets}</p>
                </div>
                <MessageSquare className="w-8 h-8 text-orange-600" />
              </div>
            </CardContent>
          </Card>

          {isProfessionalCandidate && (
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600">{t.averageRating}</p>
                    <p className="text-2xl font-bold">{stats.average_rating.toFixed(1)}</p>
                  </div>
                  <Star className="w-8 h-8 text-yellow-600" />
                </div>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Tabs */}
        <Tabs defaultValue="overview" className="min-w-0 space-y-4">
          <div className="max-w-full pb-1" data-testid="admin-user-tabs-scroll">
          <TabsList className="flex h-auto w-full flex-wrap justify-start">
            <TabsTrigger value="overview" className="basis-1/3" data-testid="overview-tab">{t.overview}</TabsTrigger>
            <TabsTrigger value="jobs" className="basis-1/3" data-testid="jobs-tab">{t.jobs} ({stats.total_jobs})</TabsTrigger>
            {isProfessionalCandidate && (
              <TabsTrigger value="bids" className="basis-1/3" data-testid="bids-tab">{t.bids} ({stats.total_bids})</TabsTrigger>
            )}
            {isProfessionalCandidate && (
              <TabsTrigger value="documents" className="basis-1/3" data-testid="documents-tab">Documents</TabsTrigger>
            )}
            <TabsTrigger value="support" className="basis-1/3" data-testid="support-tab">{t.support} ({stats.total_support_tickets})</TabsTrigger>
            <TabsTrigger value="payments" className="basis-1/3" data-testid="payments-tab">{t.payments} ({stats.total_payments || 0})</TabsTrigger>
            <TabsTrigger value="reviews" className="basis-1/3" data-testid="reviews-tab">{t.reviews}</TabsTrigger>
          </TabsList>
          </div>

          {/* Overview Tab */}
          <TabsContent value="overview">
            <div className="grid md:grid-cols-2 gap-6">
              <Card>
                <CardHeader>
                  <CardTitle>{t.personalInfo}</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="flex justify-between">
                    <span className="text-gray-600">{t.name}:</span>
                    <span className="font-medium">{user.name}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">{t.email}:</span>
                    <span className="font-medium">{user.email}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">{t.phone}:</span>
                    <span className="font-medium">{user.phone || '-'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">{t.role}:</span>
                    <span className="font-medium">{t[user.role]}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600">{t.subscription}:</span>
                    <div className="flex items-center gap-2">
                      {getStatusBadge(user.subscription_status || 'trial')}
                      {['expired', 'cancelled'].includes(user.subscription_status) && (
                        <Button
                          variant="outline"
                          size="sm"
                          className="h-6 text-[10px] px-2 border-orange-300 text-orange-600 hover:bg-orange-50"
                          data-testid={`trigger-renewal-retry-btn-${user.id}`}
                          onClick={async () => {
                            try {
                              const res = await axios.post(`${API}/admin/trigger-renewal-retry/${user.id}`);
                              if (res.data?.charged) {
                                toast.success('Card charged — subscription reactivated!');
                                fetchUserDetails();
                              } else {
                                toast.success('Renewal email sent!');
                              }
                            } catch (e) {
                              toast.error(e.response?.data?.detail || 'Failed to send');
                            }
                          }}
                        >
                          <RefreshCw className="w-2.5 h-2.5 mr-1" />
                          Retry
                        </Button>
                      )}
                    </div>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">{t.joinedDate}:</span>
                    <span className="font-medium">{formatDate(user.created_at)}</span>
                  </div>
                </CardContent>
              </Card>

              {isProfessionalCandidate && professional_profile && (
                <Card>
                  <CardHeader>
                    <CardTitle>{t.professional} {t.status}</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <div className="flex justify-between">
                      <span className="text-gray-600">{t.verification}:</span>
                      {getStatusBadge(documents?.verification_status || 'incomplete')}
                    </div>
                    {/* Quick approve/reject actions */}
                    {documents?.verification_status === 'submitted' && (
                      <div className="flex gap-2 pt-1">
                        <Button size="sm" className="bg-green-600 hover:bg-green-700 text-white text-xs h-7"
                          onClick={async () => {
                            try {
                              await axios.post(`${API}/admin/verifications/${user.id}/approve`, { notes: 'Approved by admin' });
                              toast.success('Approved!');
                              window.location.reload();
                            } catch (e) { toast.error('Failed'); }
                          }} disabled={!documents?.complete} data-testid="quick-approve-btn">Approve</Button>
                        <Button size="sm" variant="outline" className="border-red-300 text-red-600 hover:bg-red-50 text-xs h-7"
                          onClick={async () => {
                            const reason = window.prompt('Required corrections:');
                            if (!reason) return;
                            try {
                              await axios.post(`${API}/admin/verifications/${user.id}/request-changes`, { notes: reason });
                              toast.success('Corrections requested');
                              window.location.reload();
                            } catch (e) { toast.error('Failed'); }
                          }} data-testid="quick-request-changes-btn">Request corrections</Button>
                      </div>
                    )}
                    {professional_profile.categories && (
                      <div>
                        <span className="text-gray-600">{t.categories}:</span>
                        <div className="flex flex-wrap gap-2 mt-2">
                          {professional_profile.categories.map((cat, idx) => (
                            <Badge key={idx} variant="outline">{cat}</Badge>
                          ))}
                        </div>
                      </div>
                    )}
                    {professional_profile.service_areas && (
                      <div>
                        <span className="text-gray-600">{t.serviceAreas}:</span>
                        <div className="mt-2 text-sm">
                          {(professional_profile.service_areas.values || professional_profile.service_areas.cantons || []).join(', ')}
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>
              )}
            </div>
          </TabsContent>

          {/* Jobs Tab */}
          <TabsContent value="jobs">
            <Card>
              <CardHeader>
                <CardTitle>{t.jobs}</CardTitle>
              </CardHeader>
              <CardContent>
                {jobs.length === 0 ? (
                  <p className="text-center text-gray-500 py-8">{t.noData}</p>
                ) : (
                  <div className="space-y-4">
                    {jobs.map((job) => (
                      <div key={job.id} className="border rounded-lg p-4 hover:bg-gray-50">
                        <div className="flex justify-between items-start">
                          <div className="flex-1">
                            <h3 className="font-bold">{job.title}</h3>
                            <p className="text-sm text-gray-600 mt-1">{job.description?.substring(0, 100)}...</p>
                            <div className="flex items-center gap-4 mt-2 text-sm text-gray-600">
                              <span>{t.jobCategory}: {job.category}</span>
                              <span>{t.jobBudget}: CHF {job.budget}</span>
                              <span>{formatDate(job.created_at)}</span>
                            </div>
                          </div>
                          {getStatusBadge(job.status)}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Bids Tab */}
          {isProfessionalCandidate && (
            <TabsContent value="bids">
              <Card>
                <CardHeader>
                  <CardTitle>{t.bids}</CardTitle>
                </CardHeader>
                <CardContent>
                  {bids.length === 0 ? (
                    <p className="text-center text-gray-500 py-8">{t.noData}</p>
                  ) : (
                    <div className="space-y-4">
                      {bids.map((bid) => (
                        <div key={bid.id} className="border rounded-lg p-4 hover:bg-gray-50">
                          <div className="flex justify-between items-start">
                            <div className="flex-1">
                              <h3 className="font-bold">{bid.job_title || 'Job'}</h3>
                              <p className="text-sm text-gray-600 mt-1">{bid.message?.substring(0, 100)}...</p>
                              <div className="flex items-center gap-4 mt-2 text-sm text-gray-600">
                                <span>{t.bidAmount}: CHF {bid.proposed_price ?? bid.amount ?? bid.price}</span>
                                <span>{formatDate(bid.created_at)}</span>
                              </div>
                            </div>
                            {getStatusBadge(bid.status || 'pending')}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
          )}

          {/* Documents Tab — professional only */}
          {isProfessionalCandidate && (
            <TabsContent value="documents" data-testid="documents-tab-content">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Shield className="w-5 h-5" /> Verification Documents
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {!documents ? (
                    <p className="text-center text-gray-500 py-8">No documents submitted yet.</p>
                  ) : (
                    <div className="space-y-5">
                      {/* Status + Notes */}
                      <div className="flex flex-col gap-3 p-3 bg-gray-50 rounded-lg border sm:flex-row sm:items-center sm:justify-between">
                        <span className="text-sm text-gray-600 font-medium">Verification Status</span>
                        <div className="flex flex-wrap items-center gap-3">
                          {getStatusBadge(documents.verification_status || 'incomplete')}
                          {documents.verification_status === 'submitted' && (
                            <div className="flex flex-wrap gap-2">
                              <Button size="sm" className="h-auto whitespace-normal bg-green-600 py-2 text-xs text-white hover:bg-green-700"
                                onClick={async () => {
                                  try {
                                    await axios.post(`${API}/admin/verifications/${user.id}/approve`, { notes: 'Documents reviewed and approved' });
                                    toast.success('Approved!'); window.location.reload();
                                  } catch (e) { const detail = e.response?.data?.detail; toast.error(detail?.message || detail || 'Failed'); }
                                }} disabled={!documents.complete} data-testid="doc-approve-btn">Approve</Button>
                              <Button size="sm" variant="outline" className="h-auto whitespace-normal border-red-300 py-2 text-xs text-red-600"
                                onClick={async () => {
                                  const reason = window.prompt('Required corrections:');
                                  if (!reason) return;
                                  try {
                                    await axios.post(`${API}/admin/verifications/${user.id}/request-changes`, { notes: reason });
                                    toast.success('Corrections requested'); window.location.reload();
                                  } catch (e) { toast.error('Failed'); }
                                }} data-testid="doc-request-changes-btn">Request corrections</Button>
                            </div>
                          )}
                        </div>
                      </div>
                      {documents.admin_notes && (
                        <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg text-sm text-yellow-800">
                          <strong>Notes:</strong> {documents.admin_notes}
                        </div>
                      )}
                      {documents.id_verification && (documents.id_verification.country || documents.id_verification.document_type) && (
                        <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg text-sm text-blue-800" data-testid="id-verification-meta">
                          <strong>ID Details:</strong> {documents.id_verification.document_type || '—'} · {documents.id_verification.country || '—'}
                        </div>
                      )}

                      {/* Document list */}
                      {[
                        { label: 'Profile Photo', key: 'profile_photo_url', icon: '👤' },
                        { label: 'Company Logo', key: 'company_logo_url', icon: '🏢' },
                        { label: 'ID Document', key: 'id_document_url', icon: '🪪' },
                        { label: 'Business License', key: 'business_license_url', icon: '📜' },
                        { label: 'Insurance Certificate', key: 'insurance_url', icon: '🛡️' },
                      ].map(({ label, key, icon }) => {
                        const url = documents[key];
                        return (
                          <div key={key} className="border rounded-xl p-4" data-testid={`doc-${key}`}>
                            <div className="flex items-center justify-between mb-3">
                              <span className="font-medium text-gray-800 flex items-center gap-2">
                                <span>{icon}</span>{label}
                              </span>
                              {url ? (
                                <PrivateFileLink reference={url} label="View / Download"
                                  className="text-xs bg-blue-100 text-blue-700 px-3 py-1 rounded-full hover:bg-blue-200 font-medium"
                                  testId={`doc-link-${key}`} />
                              ) : (
                                <span className="text-xs text-gray-400 italic">Not submitted</span>
                              )}
                            </div>
                            {url && !isPrivateFileReference(url) && (url.match(/\.(jpg|jpeg|png|gif|webp)$/i) || url.includes('blob') || url.includes('supabase') || url.includes('storage')) && (
                              <img src={url} alt={label} className="w-full max-h-64 object-contain rounded-lg border bg-gray-50"
                                onError={(e) => { e.target.style.display = 'none'; }} />
                            )}
                          </div>
                        );
                      })}

                      {/* References / extra docs */}
                      {documents.references_urls?.length > 0 && (
                        <div className="border rounded-xl p-4">
                          <p className="font-medium text-gray-800 mb-3">📎 Reference Documents ({documents.references_urls.length})</p>
                          <div className="space-y-2">
                            {documents.references_urls.map((url, i) => <PrivateFileLink key={url} reference={url} label={`📄 Reference ${i + 1}`} className="flex items-center gap-2 text-sm text-blue-600 hover:underline" testId={`ref-doc-${i}`} />)}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
          )}

          {/* Support Tab */}
          <TabsContent value="support">
            <Card>
              <CardHeader>
                <CardTitle>{t.support}</CardTitle>
              </CardHeader>
              <CardContent>
                {support_tickets.length === 0 ? (
                  <p className="text-center text-gray-500 py-8">{t.noData}</p>
                ) : (
                  <div className="space-y-4">
                    {support_tickets.map((ticket) => (
                      <div key={ticket.id} className="border rounded-lg p-4 hover:bg-gray-50">
                        <div className="flex justify-between items-start">
                          <div className="flex-1">
                            <h3 className="font-bold">{ticket.title}</h3>
                            <p className="text-sm font-medium text-gray-700 mt-1">{ticket.subject}</p>
                            <p className="text-sm text-gray-600 mt-1">{ticket.message?.substring(0, 100)}...</p>
                            <span className="text-xs text-gray-500 mt-2 block">{formatDate(ticket.created_at)}</span>
                          </div>
                          {getStatusBadge(ticket.status)}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Payments Tab */}
          <TabsContent value="payments" data-testid="payments-tab-content">
            <div className="space-y-6">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <CreditCard className="w-5 h-5" /> {t.savedCard}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {!saved_card ? (
                    <p className="text-center text-gray-500 py-6" data-testid="no-saved-card">{t.noSavedCard}</p>
                  ) : (
                    <div className="flex flex-wrap items-center justify-between gap-3 p-3 bg-gray-50 rounded-lg border" data-testid="saved-card-info">
                      <div>
                        <p className="font-medium text-gray-800">{t.cardNumber}: {saved_card.card_display || '—'}</p>
                        <p className="text-xs text-gray-500 mt-1">{t.registeredOn}: {formatDate(saved_card.registered_at)}</p>
                      </div>
                      <Badge className={saved_card.auto_renewal ? 'bg-green-600' : 'bg-gray-500'}>
                        {t.autoRenewal}: {saved_card.auto_renewal ? t.enabled : t.disabled}
                      </Badge>
                    </div>
                  )}
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>{t.payments} ({t.totalPaid}: CHF {(stats.total_paid_chf || 0).toFixed(2)})</CardTitle>
                </CardHeader>
                <CardContent>
                  {!payments || payments.length === 0 ? (
                    <p className="text-center text-gray-500 py-8" data-testid="no-payments">{t.noPayments}</p>
                  ) : (
                    <div className="overflow-x-auto" data-testid="payments-table">
                      <table className="w-full text-sm">
                        <thead className="border-b">
                          <tr>
                            <th className="text-left py-2 font-medium text-gray-600">{t.transactionType}</th>
                            <th className="text-right py-2 font-medium text-gray-600">{t.amount}</th>
                            <th className="text-left py-2 font-medium text-gray-600">{t.paymentStatus}</th>
                            <th className="text-right py-2 font-medium text-gray-600">{t.paymentDate}</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y">
                          {payments.map((p) => (
                            <tr key={p.id} data-testid={`payment-row-${p.id}`}>
                              <td className="py-2">{p.type}</td>
                              <td className="py-2 text-right font-medium">CHF {((p.amount || 0) / 100).toFixed(2)}</td>
                              <td className="py-2">
                                <Badge className={p.status === 'completed' || p.status === 'captured' ? 'bg-green-600' : p.status === 'pending' ? 'bg-orange-600' : 'bg-red-600'}>
                                  {p.status}
                                </Badge>
                              </td>
                              <td className="py-2 text-right text-gray-500">{formatDate(p.completed_at || p.created_at)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Reviews Tab */}
          <TabsContent value="reviews">
            <div className="space-y-6">
              {isProfessionalCandidate && (
                <Card>
                  <CardHeader>
                    <CardTitle>{t.reviews} {t.received}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    {reviews_received.length === 0 ? (
                      <p className="text-center text-gray-500 py-8">{t.noData}</p>
                    ) : (
                      <div className="space-y-4">
                        {reviews_received.map((review) => (
                          <div key={review.id} className="border rounded-lg p-4">
                            <div className="flex items-center justify-between mb-2">
                              <div className="flex items-center">
                                {[...Array(5)].map((_, i) => (
                                  <Star
                                    key={i}
                                    className={`w-4 h-4 ${i < review.rating ? 'text-yellow-500 fill-yellow-500' : 'text-gray-300'}`}
                                  />
                                ))}
                              </div>
                              <span className="text-sm text-gray-500">{formatDate(review.created_at)}</span>
                            </div>
                            <p className="text-sm text-gray-700">{review.review}</p>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              )}

              {user.role === 'client' && (
                <Card>
                  <CardHeader>
                    <CardTitle>{t.reviews} {t.given}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    {reviews_given.length === 0 ? (
                      <p className="text-center text-gray-500 py-8">{t.noData}</p>
                    ) : (
                      <div className="space-y-4">
                        {reviews_given.map((review) => (
                          <div key={review.id} className="border rounded-lg p-4">
                            <div className="flex items-center justify-between mb-2">
                              <div className="flex items-center">
                                {[...Array(5)].map((_, i) => (
                                  <Star
                                    key={i}
                                    className={`w-4 h-4 ${i < review.rating ? 'text-yellow-500 fill-yellow-500' : 'text-gray-300'}`}
                                  />
                                ))}
                              </div>
                              <span className="text-sm text-gray-500">{formatDate(review.created_at)}</span>
                            </div>
                            <p className="text-sm text-gray-700">{review.review}</p>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              )}
            </div>
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
};

export default AdminUserDetails;
